import { useState, useEffect, useCallback, useRef } from 'react';
import calendarService from '../services/calendarService';
import { NOTIFICATION_TYPES, NOTIFICATION_URGENCY_ORDER } from '../constants/notificationTypes';
import { useToast } from './useToast';

export const useCalendarNotifications = () => {
  const { showToast } = useToast();
  
  const [persistentNotifications, setPersistentNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isNotificationPanelOpen, setIsNotificationPanelOpen] = useState(false);
  
  const reminderTimers = useRef(new Map());
  const checkInterval = useRef(null);

  const loadPersonalReminders = useCallback(async (forceRefresh = false) => {
    try {
      const remindersResponse = await calendarService.getPersonalReminders(168, forceRefresh);
      
      const today = new Date();
      const year = today.getFullYear();
      const month = today.getMonth() + 1;
      
      const todayEventsResponse = await calendarService.getMonthEvents(year, month);
      
      const eventiPartecipante = remindersResponse?.eventiComePartecipante?.eventi || [];
      const eventiPubblici = remindersResponse?.eventiPubblici?.eventi || [];
      const eventiPromemoria = [...eventiPartecipante, ...eventiPubblici];
      
      const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const todayEnd = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);
      
      const eventiOggi = (todayEventsResponse || []).filter(evento => {
        const eventoData = new Date(evento.CAL_DataOraInizio);
        return eventoData >= todayStart && eventoData <= todayEnd;
      });
      
      const tuttiEventi = [...eventiPromemoria, ...eventiOggi];
      
      const eventiUnique = tuttiEventi.reduce((acc, evento) => {
        if (!acc.find(e => e.CAL_Id_CalendarioEventi === evento.CAL_Id_CalendarioEventi)) {
          acc.push(evento);
        }
        return acc;
      }, []);
      
      if (eventiUnique.length === 0) {
        setPersistentNotifications([]);
        setUnreadCount(0);
        return;
      }

      const now = new Date();
      const notifications = [];
      
      eventiUnique.forEach((evento) => {
        const eventTime = new Date(evento.CAL_DataOraInizio);
        const timeDiff = eventTime.getTime() - now.getTime();
        const minutesUntil = Math.floor(timeDiff / (1000 * 60));
        const hoursUntil = Math.floor(minutesUntil / 60);
        const daysUntil = Math.floor(hoursUntil / 24);
        
        if (minutesUntil > 0) {
          let notificationType = 'PARTICIPANT_EVENT';
          let message = `Evento: ${evento.CAL_Titolo}`;
          let priority = 'low';
          
          if (evento.tipoPartecipazione === 'pubblico') {
            notificationType = 'PUBLIC_EVENT';
            message = `🌐 Evento pubblico: ${evento.CAL_Titolo}`;
          }
          
          if (minutesUntil <= 30) {
            notificationType = 'REMINDER_30MIN';
            message = `🔴 IMMINENTE: ${evento.CAL_Titolo} tra ${minutesUntil} minuti!`;
            priority = 'high';
          } else if (minutesUntil <= 60) {
            notificationType = 'REMINDER_1HOUR';
            message = `🟡 PRESTO: ${evento.CAL_Titolo} tra ${minutesUntil} minuti`;
            priority = 'medium';
          } else if (minutesUntil <= 180) {
            notificationType = 'REMINDER_3HOURS';
            message = `🟠 NELLE PROSSIME ORE: ${evento.CAL_Titolo} tra ${Math.ceil(minutesUntil/60)} ore`;
            priority = 'medium';
          } else if (minutesUntil <= 1440) {
            notificationType = 'REMINDER_1DAY';
            if (evento.tipoPartecipazione === 'pubblico') {
              message = `🟢 OGGI (pubblico): ${evento.CAL_Titolo} alle ${eventTime.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`;
            } else {
              message = `🟢 OGGI: ${evento.CAL_Titolo} alle ${eventTime.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`;
            }
            priority = 'medium';
          } else {
            notificationType = 'FUTURE_EVENT';
            if (daysUntil <= 7) {
              message = `📅 QUESTA SETTIMANA: ${evento.CAL_Titolo} il ${eventTime.toLocaleDateString('it-IT')} alle ${eventTime.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`;
            } else {
              message = `📅 FUTURO: ${evento.CAL_Titolo} il ${eventTime.toLocaleDateString('it-IT')}`;
            }
            priority = 'low';
          }

          const notification = {
            id: `${evento.tipoPartecipazione || 'participant'}_${evento.CAL_Id_CalendarioEventi}`,
            eventId: evento.CAL_Id_CalendarioEventi,
            type: notificationType,
            title: evento.CAL_Titolo,
            message: message,
            scheduledTime: eventTime,
            isRead: false,
            timestamp: new Date(),
            priority: priority,
            tipoPartecipazione: evento.tipoPartecipazione || 'partecipante',
            minutesUntil: minutesUntil,
            hoursUntil: hoursUntil,
            daysUntil: daysUntil,
            ...NOTIFICATION_TYPES[notificationType]
          };

          notifications.push(notification);
        }
      });

      notifications.sort((a, b) => {
        const priorityOrder = { high: 0, medium: 1, low: 2 };
        const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority];
        if (priorityDiff !== 0) return priorityDiff;
        return a.minutesUntil - b.minutesUntil;
      });
      
      setPersistentNotifications(notifications);
      setUnreadCount(notifications.length);
      
    } catch (err) {
      setPersistentNotifications([]);
      setUnreadCount(0);
    }
  }, []);

  const forceRefreshReminders = useCallback(() => {
    return loadPersonalReminders(true);
  }, [loadPersonalReminders]);

  const scheduleReminder = useCallback((notification) => {
    const now = new Date();
    const scheduleTime = new Date(notification.scheduledTime);
    const delay = scheduleTime.getTime() - now.getTime();

    if (delay > 0) {
      const timerId = setTimeout(() => {
        showToast(
          notification.message,
          notification.priority === 'high' ? 'warning' : 'info',
          {
            duration: notification.priority === 'high' ? 10000 : 5000,
            icon: notification.icon
          }
        );

        setPersistentNotifications(prev => 
          prev.map(n => 
            n.id === notification.id 
              ? { ...n, isShown: true, timestamp: new Date() }
              : n
          )
        );

        reminderTimers.current.delete(notification.id);
      }, delay);

      reminderTimers.current.set(notification.id, timerId);
    }
  }, [showToast]);

  const checkUpcomingReminders = useCallback(async () => {
    try {
      const upcomingReminders = [];
      
      upcomingReminders.forEach(reminder => {
        const notificationId = `reminder_${reminder.id}`;
        
        if (!reminderTimers.current.has(notificationId)) {
          const notification = {
            id: notificationId,
            eventId: reminder.evento_id,
            type: 'REMINDER_30MIN',
            title: reminder.titolo_evento,
            message: `Promemoria: ${reminder.titolo_evento}`,
            scheduledTime: new Date(reminder.data_promemoria),
            eventTime: new Date(reminder.data_inizio),
            isRead: false,
            timestamp: new Date(),
            ...NOTIFICATION_TYPES.REMINDER_30MIN
          };

          scheduleReminder(notification);
        }
      });
      
    } catch (err) {
      // Silent fail
    }
  }, [scheduleReminder]);

  const addPersistentNotification = useCallback((notification) => {
    setPersistentNotifications(prev => {
      if (prev.some(n => n.id === notification.id)) {
        return prev;
      }
      
      const newNotifications = [notification, ...prev];
      
      return newNotifications.sort((a, b) => {
        const urgencyA = NOTIFICATION_URGENCY_ORDER.indexOf(a.type);
        const urgencyB = NOTIFICATION_URGENCY_ORDER.indexOf(b.type);
        
        if (urgencyA !== urgencyB) {
          return urgencyA - urgencyB;
        }
        
        return new Date(b.timestamp) - new Date(a.timestamp);
      });
    });
    
    setUnreadCount(prev => prev + 1);
  }, []);

  const notifyEventCreated = useCallback((event) => {
    const notification = {
      id: `event_created_${event.id}_${Date.now()}`,
      eventId: event.id,
      type: 'EVENT_CREATED',
      title: 'Nuovo Evento',
      message: `Evento "${event.CAL_Titolo || event.titolo}" creato per il ${new Date(event.CAL_DataOraInizio || event.data_inizio).toLocaleDateString('it-IT')}`,
      isRead: false,
      timestamp: new Date(),
      ...NOTIFICATION_TYPES.EVENT_CREATED
    };

    showToast(notification.message, 'success', { icon: notification.icon });
    addPersistentNotification(notification);
  }, [showToast, addPersistentNotification]);

  const notifyEventUpdated = useCallback((event) => {
    const notification = {
      id: `event_updated_${event.id}_${Date.now()}`,
      eventId: event.id,
      type: 'EVENT_UPDATED',
      title: 'Evento Modificato',
      message: `Evento "${event.CAL_Titolo || event.titolo}" è stato modificato`,
      isRead: false,
      timestamp: new Date(),
      ...NOTIFICATION_TYPES.EVENT_UPDATED
    };

    showToast(notification.message, 'info', { icon: notification.icon });
    addPersistentNotification(notification);
  }, [showToast, addPersistentNotification]);

  const notifyEventCancelled = useCallback((event) => {
    const notification = {
      id: `event_cancelled_${event.id}_${Date.now()}`,
      eventId: event.id,
      type: 'EVENT_CANCELLED',
      title: 'Evento Cancellato',
      message: `Evento "${event.CAL_Titolo || event.titolo}" è stato cancellato`,
      isRead: false,
      timestamp: new Date(),
      ...NOTIFICATION_TYPES.EVENT_CANCELLED
    };

    showToast(notification.message, 'warning', { icon: notification.icon });
    addPersistentNotification(notification);
  }, [showToast, addPersistentNotification]);

  const markAsRead = useCallback((notificationId) => {
    setPersistentNotifications(prev => 
      prev.map(n => 
        n.id === notificationId 
          ? { ...n, isRead: true }
          : n
      )
    );
    
    setUnreadCount(prev => Math.max(0, prev - 1));
  }, []);

  const markAllAsRead = useCallback(() => {
    setPersistentNotifications(prev => 
      prev.map(n => ({ ...n, isRead: true }))
    );
    setUnreadCount(0);
  }, []);

  const removeNotification = useCallback((notificationId) => {
    setPersistentNotifications(prev => 
      prev.filter(n => n.id !== notificationId)
    );
  }, []);

  const cleanOldNotifications = useCallback(() => {
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    
    setPersistentNotifications(prev => 
      prev.filter(n => new Date(n.timestamp) > weekAgo)
    );
  }, []);

  useEffect(() => {
    loadPersonalReminders();
    
    checkInterval.current = setInterval(() => {
      loadPersonalReminders();
    }, 30000);
    
    return () => {
      if (checkInterval.current) {
        clearInterval(checkInterval.current);
      }
      
      reminderTimers.current.forEach(timerId => clearTimeout(timerId));
      reminderTimers.current.clear();
    };
  }, [loadPersonalReminders]);

  useEffect(() => {
    const cleanupInterval = setInterval(cleanOldNotifications, 3600000);
    return () => clearInterval(cleanupInterval);
  }, [cleanOldNotifications]);

  return {
    persistentNotifications,
    unreadCount,
    isNotificationPanelOpen,
    setIsNotificationPanelOpen,
    markAsRead,
    markAllAsRead,
    removeNotification,
    cleanOldNotifications,
    notifyEventCreated,
    notifyEventUpdated,
    notifyEventCancelled,
    addPersistentNotification,
    refreshReminders: loadPersonalReminders,
    forceRefreshReminders,
    activeTimers: reminderTimers.current.size
  };
};